/**
 * State Migration Engine - Core Types
 *
 * All migration definitions, history records, and result types.
 * No side effects at module level - pure type and interface declarations.
 */

/* =========================================================
   MIGRATION DEFINITION
   ========================================================= */

/**
 * A single version-step migration.
 *
 * Invariants:
 *   - toVersion === fromVersion + 1   (no skip migrations)
 *   - migrate() is deterministic      (same input → same output)
 *   - migrate() is safe               (never drops unknown fields)
 *   - validate() is optional          (post-transform integrity check)
 */
export interface MigrationDefinition<From = unknown, To = unknown> {
  readonly fromVersion: number;
  readonly toVersion:   number;
  readonly description: string;

  /** Transform data from fromVersion shape to toVersion shape. */
  migrate(data: From): To;

  /**
   * Optional post-transform validator.
   * If present and returns false, the migration is treated as failed
   * and the backup is restored.
   */
  validate?(result: To): boolean;
}

/* =========================================================
   MIGRATION HISTORY
   ========================================================= */

export type MigrationStatus = "in-progress" | "complete" | "failed" | "rolled-back";

/**
 * Persistent record of a migration that was attempted or completed.
 *
 * Stored in localStorage under nuvra-migration-history.
 * Used for:
 *   - Idempotency: detect already-applied migrations
 *   - Crash recovery: detect in-progress migrations interrupted by page crash
 *   - Observability: audit trail of all schema evolution events
 */
export interface MigrationHistoryRecord {
  /** Composite key: "{storageKey}::{fromVersion}::{toVersion}" */
  id:          string;
  storageKey:  string;
  fromVersion: number;
  toVersion:   number;
  status:      MigrationStatus;
  startedAt:   number;       // epoch ms
  completedAt: number | null;
  error:       string | null;
  /**
   * Key of the backup copy in localStorage.
   * Present only while status === "in-progress".
   * Removed after successful commit.
   * Used for rollback if migration fails or crashes.
   */
  backupKey:   string | null;
}

/* =========================================================
   MIGRATION RESULT
   ========================================================= */

export interface MigrationResult<T = unknown> {
  success:      boolean;
  data:         T | null;
  fromVersion:  number;
  appliedTo:    number;    // final version after applying all steps
  stepsApplied: number;
  durationMs:   number;
  error?:       string;
  rolledBack?:  boolean;
}

/* =========================================================
   ERRORS
   ========================================================= */

export class MigrationPathError extends Error {
  constructor(
    public readonly fromVersion: number,
    public readonly toVersion:   number,
    public readonly gap:         number,
  ) {
    super(
      `No migration path from v${fromVersion} to v${toVersion}: missing step v${gap}→v${gap + 1}`,
    );
    this.name = "MigrationPathError";
  }
}

export class MigrationValidationError extends Error {
  constructor(
    public readonly fromVersion: number,
    public readonly toVersion:   number,
  ) {
    super(`Migration v${fromVersion}→v${toVersion}: post-transform validation failed`);
    this.name = "MigrationValidationError";
  }
}
